/**
 * ArtifactDetailPanel — full detail view for a selected DAG artifact.
 *
 * Shows identity, connectivity (parents/children), metadata, payload
 * (value_json), staleness, and timestamps. All data is derived from
 * the already-loaded provenance DAG — no additional API calls.
 */

import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertDialog, Badge, Box, Button, Code, Flex, Heading, Separator, Text } from "@radix-ui/themes";
import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useProvenance, useDeleteNode } from "../../hooks/useProvenance";
import { useUserLookup } from "../../hooks/useUserLookup";
import { useAnonymous } from "../../context/AnonymousContext";
import type { DAGNode, DAGEdge } from "../../api/artifacts";


const KIND_COLORS: Record<string, "blue" | "violet" | "cyan" | "green" | "amber" | "gray"> = {
  geometry: "blue",
  surface_mesh: "violet",
  volume_mesh: "cyan",
  run: "green",
  saved_plot: "amber",
};

interface ConnectedNode {
  node: DAGNode;
  relationship?: string;
}

function deriveConnectivity(nodeId: string, nodes: DAGNode[], edges: DAGEdge[]) {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  const parents: ConnectedNode[] = [];
  const children: ConnectedNode[] = [];

  for (const e of edges) {
    if (e.child_id === nodeId) {
      const parent = nodeMap.get(e.parent_id);
      if (parent) parents.push({ node: parent, relationship: e.relationship });
    }
    if (e.parent_id === nodeId) {
      const child = nodeMap.get(e.child_id);
      if (child) children.push({ node: child, relationship: e.relationship });
    }
  }

  return { parents, children };
}

function Section({ title, children: content }: { title: string; children: React.ReactNode }) {
  return (
    <Box mb="4">
      <Text size="1" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {title}
      </Text>
      <Box mt="2">{content}</Box>
    </Box>
  );
}

function Field({ label, children: content }: { label: string; children: React.ReactNode }) {
  return (
    <Flex justify="between" align="start" gap="3" py="1">
      <Text size="1" color="gray" style={{ flexShrink: 0 }}>{label}</Text>
      <Box style={{ textAlign: "right", wordBreak: "break-all" }}>{content}</Box>
    </Flex>
  );
}

function ConnectedNodeItem({ item, onSelect }: { item: ConnectedNode; onSelect: (id: string) => void }) {
  const color = KIND_COLORS[item.node.kind] ?? "gray";
  return (
    <Flex
      align="center"
      gap="2"
      py="1"
      px="2"
      style={{ borderRadius: 4, cursor: "pointer" }}
      onClick={() => onSelect(item.node.id)}
      className="artifact-detail-link"
    >
      <Badge size="1" color={color} variant="soft">{item.node.kind}</Badge>
      <Text size="1" weight="medium" style={{ flex: 1 }}>{item.node.name}</Text>
      {item.relationship && (
        <Text size="1" color="gray">{item.relationship}</Text>
      )}
    </Flex>
  );
}

function JsonTree({ data, depth = 0 }: { data: unknown; depth?: number }) {
  if (data === null || data === undefined) {
    return <Text size="1" color="gray">null</Text>;
  }

  if (typeof data !== "object") {
    return <Code size="1">{String(data)}</Code>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return <Text size="1" color="gray">[]</Text>;
    return (
      <Box pl={depth > 0 ? "3" : "0"}>
        {data.map((item, i) => (
          <Flex key={i} gap="2" py="1" align="start">
            <Text size="1" color="gray" style={{ flexShrink: 0 }}>[{i}]</Text>
            <JsonTree data={item} depth={depth + 1} />
          </Flex>
        ))}
      </Box>
    );
  }

  const entries = Object.entries(data as Record<string, unknown>);
  if (entries.length === 0) return <Text size="1" color="gray">{"{}"}</Text>;

  return (
    <Box pl={depth > 0 ? "3" : "0"}>
      {entries.map(([key, val]) => {
        const isNested = val !== null && typeof val === "object";
        if (isNested) {
          return (
            <Box key={key} py="1">
              <Text size="1" color="gray">{key}:</Text>
              <JsonTree data={val} depth={depth + 1} />
            </Box>
          );
        }
        return (
          <Flex key={key} justify="between" gap="3" py="1" align="start">
            <Text size="1" color="gray" style={{ flexShrink: 0 }}>{key}</Text>
            <Code size="1" style={{ wordBreak: "break-all" }}>{String(val ?? "")}</Code>
          </Flex>
        );
      })}
    </Box>
  );
}

const RELATIONSHIP_DESCRIPTIONS: Record<string, string> = {
  produced: "Upstream process created downstream data",
  generated: "Upstream entity generated downstream entity",
  depends_on: "Downstream value depends on upstream parameter",
  derived_from: "Downstream is a transformation of upstream",
  plotted_by: "Data visualised by this saved plot",
  defines: "Geometry defines this reference quantity",
};

function EdgeEndpoint({ node, relationship, direction, onSelect }: {
  node: DAGNode;
  relationship?: string;
  direction: "source" | "target";
  onSelect: (id: string) => void;
}) {
  const color = KIND_COLORS[node.kind] ?? "gray";
  return (
    <Box mb="2">
      <Text size="1" color="gray" mb="1">{direction === "source" ? "Source (parent)" : "Target (child)"}</Text>
      <Flex
        align="center" gap="2" py="1" px="2"
        style={{ borderRadius: 4, cursor: "pointer" }}
        onClick={() => onSelect(node.id)}
        className="artifact-detail-link"
      >
        <Badge size="1" color={color} variant="soft">{node.kind}</Badge>
        <Text size="1" weight="medium" style={{ flex: 1 }}>{node.name}</Text>
      </Flex>
      {relationship && (
        <Text size="1" color="gray" style={{ display: "block", marginTop: 2, paddingLeft: 8 }}>
          {relationship}
          {RELATIONSHIP_DESCRIPTIONS[relationship] && (
            <> — {RELATIONSHIP_DESCRIPTIONS[relationship]}</>
          )}
        </Text>
      )}
    </Box>
  );
}

function EdgeDetailView({ edgeId }: { edgeId: string }) {
  const { projectId, currentBranch } = useProvenanceContext();
  const { data } = useProvenance(projectId, currentBranch);
  const [, setSearchParams] = useSearchParams();

  const edge = useMemo(() => data?.edges.find((e) => e.id === edgeId) ?? null, [data?.edges, edgeId]);
  const sourceNode = useMemo(() => edge && data ? data.nodes.find((n) => n.id === edge.parent_id) ?? null : null, [edge, data]);
  const targetNode = useMemo(() => edge && data ? data.nodes.find((n) => n.id === edge.child_id) ?? null : null, [edge, data]);

  const handleSelectNode = useCallback((id: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("nodes", id);
      next.delete("edge");
      next.set("panel", "detail");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  if (!edge) {
    return (
      <Box p="4">
        <Text color="gray" size="2">Edge not found in current graph data.</Text>
      </Box>
    );
  }

  return (
    <Box p="4" style={{ height: "100%", overflow: "auto" }}>
      <Flex align="center" gap="2" mb="1">
        <Badge size="2" color="gray" variant="solid">edge</Badge>
        {edge.relationship && (
          <Badge size="1" color="blue" variant="soft">{edge.relationship}</Badge>
        )}
      </Flex>
      <Heading size="4" mb="1">
        {sourceNode?.name ?? "?"} → {targetNode?.name ?? "?"}
      </Heading>
      {edge.relationship && RELATIONSHIP_DESCRIPTIONS[edge.relationship] && (
        <Text size="1" color="gray">{RELATIONSHIP_DESCRIPTIONS[edge.relationship]}</Text>
      )}

      <Separator size="4" my="3" />

      <Section title="Identity">
        <Field label="Edge ID"><Code size="1">{edge.id}</Code></Field>
        {edge.relationship && <Field label="Relationship"><Text size="1">{edge.relationship}</Text></Field>}
        {edge.operation && <Field label="Operation"><Text size="1">{edge.operation}</Text></Field>}
        {edge.created_at && <Field label="Created"><Text size="1">{new Date(edge.created_at).toLocaleString()}</Text></Field>}
      </Section>

      <Section title="Endpoints">
        {sourceNode && <EdgeEndpoint node={sourceNode} relationship={edge.relationship} direction="source" onSelect={handleSelectNode} />}
        {targetNode && <EdgeEndpoint node={targetNode} relationship={edge.relationship} direction="target" onSelect={handleSelectNode} />}
      </Section>

      {edge.metadata && Object.keys(edge.metadata).length > 0 && (
        <Section title="Edge Metadata">
          <JsonTree data={edge.metadata} />
        </Section>
      )}
    </Box>
  );
}

export default function ArtifactDetailPanel() {
  const { projectId, currentBranch, selectedNodeIds, selectNodes, clearSelection } = useProvenanceContext();
  const [searchParams] = useSearchParams();
  const selectedEdgeId = searchParams.get("edge") || null;
  const { data } = useProvenance(projectId, currentBranch);
  const resolveUser = useUserLookup(projectId);
  const { isAnonymous } = useAnonymous();
  const deleteMutation = useDeleteNode(projectId, currentBranch);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const primaryId = useMemo(() => {
    if (selectedNodeIds.size === 0) return null;
    return [...selectedNodeIds][0]!;
  }, [selectedNodeIds]);

  const node = useMemo(() => {
    if (!primaryId || !data?.nodes) return null;
    return data.nodes.find((n) => n.id === primaryId) ?? null;
  }, [primaryId, data?.nodes]);

  const connectivity = useMemo(() => {
    if (!primaryId || !data) return { parents: [], children: [] };
    return deriveConnectivity(primaryId, data.nodes, data.edges);
  }, [primaryId, data]);

  const handleDelete = useCallback(() => {
    if (!primaryId) return;
    deleteMutation.mutate(primaryId, {
      onSuccess: () => {
        setConfirmOpen(false);
        clearSelection();
      },
    });
  }, [primaryId, deleteMutation, clearSelection]);

  if (selectedEdgeId) {
    return <EdgeDetailView edgeId={selectedEdgeId} />;
  }

  if (!primaryId) {
    return (
      <Box p="4">
        <Text color="gray" size="2">Select an artifact in the DAG to view its details.</Text>
      </Box>
    );
  }

  if (!node) {
    return (
      <Box p="4">
        <Text color="gray" size="2">Node not found in current graph data.</Text>
      </Box>
    );
  }

  const raw = node as Record<string, unknown>;
  const kindColor = KIND_COLORS[node.kind] ?? "gray";
  const isStale = !!raw.is_stale;
  const hasValueJson = node.value_json && Object.keys(node.value_json).length > 0;
  const hasMetadata = node.metadata && Object.keys(node.metadata).length > 0;
  const scalarFields: [string, unknown][] = [];
  for (const key of ["value_scalar", "units", "shape", "source", "code_ref", "vcs_ref"] as const) {
    if (raw[key] !== undefined && raw[key] !== null && raw[key] !== "") {
      scalarFields.push([key, raw[key]]);
    }
  }

  return (
    <Box p="4" style={{ height: "100%", overflow: "auto" }}>
      {/* Header */}
      <Flex align="center" gap="2" mb="1">
        <Badge size="2" color={kindColor} variant="solid">{node.kind}</Badge>
        {isStale && <Badge size="1" color="orange" variant="soft">stale</Badge>}
      </Flex>
      <Heading size="4" mb="1">{node.name}</Heading>
      <Text size="1" color="gray" style={{ wordBreak: "break-all" }}>{node.path}</Text>

      {!isAnonymous && (
        <Box mt="2">
          <AlertDialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialog.Trigger>
              <Button size="1" variant="soft" color="red">Delete artifact</Button>
            </AlertDialog.Trigger>
            <AlertDialog.Content maxWidth="400px">
              <AlertDialog.Title>Delete artifact</AlertDialog.Title>
              <AlertDialog.Description size="2">
                This will remove <strong>{node.name}</strong> from the DAG. The artifact can be restored later.
              </AlertDialog.Description>
              <Flex gap="3" mt="4" justify="end">
                <AlertDialog.Cancel>
                  <Button variant="soft" color="gray">Cancel</Button>
                </AlertDialog.Cancel>
                <AlertDialog.Action>
                  <Button variant="solid" color="red" onClick={handleDelete} disabled={deleteMutation.isPending}>
                    {deleteMutation.isPending ? "Deleting..." : "Delete"}
                  </Button>
                </AlertDialog.Action>
              </Flex>
            </AlertDialog.Content>
          </AlertDialog.Root>
        </Box>
      )}

      <Separator size="4" my="3" />

      {/* Identity */}
      <Section title="Identity">
        <Field label="ID"><Code size="1">{node.id}</Code></Field>
        <Field label="Kind"><Text size="1">{node.kind}</Text></Field>
        <Field label="Path"><Code size="1">{node.path}</Code></Field>
        {raw.dtype != null && <Field label="Type"><Text size="1">{String(raw.dtype)}</Text></Field>}
        {raw.created_by != null && <Field label="Created by"><Text size="1">{resolveUser(String(raw.created_by))}</Text></Field>}
        {raw.created_at != null && <Field label="Created"><Text size="1">{new Date(String(raw.created_at)).toLocaleString()}</Text></Field>}
        {raw.updated_at != null && <Field label="Updated"><Text size="1">{new Date(String(raw.updated_at)).toLocaleString()}</Text></Field>}
      </Section>

      {/* Scalar data */}
      {scalarFields.length > 0 && (
        <Section title="Data">
          {scalarFields.map(([key, val]) => (
            <Field key={key} label={key}><Code size="1">{String(val)}</Code></Field>
          ))}
        </Section>
      )}

      {/* Staleness */}
      {isStale ? (
        <Section title="Staleness">
          <Field label="Since"><Text size="1">{raw.stale_since ? new Date(String(raw.stale_since)).toLocaleString() : "—"}</Text></Field>
          <Field label="Reason"><Text size="1">{String(raw.stale_reason ?? "—")}</Text></Field>
        </Section>
      ) : null}

      {/* Connectivity */}
      <Section title={`Connectivity (${connectivity.parents.length} in · ${connectivity.children.length} out)`}>
        {connectivity.parents.length > 0 && (
          <Box mb="2">
            <Text size="1" color="gray" mb="1">Upstream (parents)</Text>
            {connectivity.parents.map((item) => (
              <ConnectedNodeItem key={item.node.id} item={item} onSelect={(id) => selectNodes([id])} />
            ))}
          </Box>
        )}
        {connectivity.children.length > 0 && (
          <Box>
            <Text size="1" color="gray" mb="1">Downstream (children)</Text>
            {connectivity.children.map((item) => (
              <ConnectedNodeItem key={item.node.id} item={item} onSelect={(id) => selectNodes([id])} />
            ))}
          </Box>
        )}
        {connectivity.parents.length === 0 && connectivity.children.length === 0 && (
          <Text size="1" color="gray">No connections.</Text>
        )}
      </Section>

      {/* Metadata */}
      {hasMetadata && (
        <Section title="Metadata">
          <JsonTree data={node.metadata} />
        </Section>
      )}

      {/* Payload (value_json) */}
      {hasValueJson && (
        <Section title="Payload (value_json)">
          <JsonTree data={node.value_json} />
        </Section>
      )}

      {/* Approval / ownership */}
      {(raw.approval_status != null || raw.owned_by != null || raw.configuration_id != null || raw.change_reason != null) ? (
        <Section title="Governance">
          {raw.approval_status != null && <Field label="Approval"><Badge size="1" color={raw.approval_status === "approved" ? "green" : "amber"}>{String(raw.approval_status)}</Badge></Field>}
          {raw.owned_by != null && <Field label="Owner"><Text size="1">{resolveUser(String(raw.owned_by))}</Text></Field>}
          {raw.configuration_id != null && <Field label="Configuration"><Code size="1">{String(raw.configuration_id)}</Code></Field>}
          {raw.change_reason != null && <Field label="Change reason"><Text size="1">{String(raw.change_reason)}</Text></Field>}
        </Section>
      ) : null}

    </Box>
  );
}
